import { Box, Button, Flex, IconButton } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
interface PaginateProps {
  count: number;
  pageSize: number;
  page: number;
  view?: any;
  onPageChange: (page: number, view?: any, id?: any) => void;
  id?: any;
  dropdown?: boolean; // ← New prop
  [key: string]: any;
}
export const Paginate = (props: PaginateProps) => {
  const {
    count,
    pageSize,
    page,
    view,
    onPageChange,
    id,
    dropdown = false, // ← Default to false
    ...rest
  } = props;
  const { t } = useTranslation("common");
  // Ensure we always work with safe numeric values to avoid runtime errors
  const totalItems = Number(count);
  const size = Number(pageSize);
  const hasValidCount = Number.isFinite(totalItems) && totalItems >= 0;
  const hasValidPageSize = Number.isFinite(size) && size > 0;
  const numberOfPages =
    hasValidCount && hasValidPageSize ? Math.ceil(totalItems / size) : 0;
  const handlePageClick = (i: number) => {
    let newPage = i;
    if (i >= numberOfPages) {
      newPage = numberOfPages;
    } else if (i <= 0) {
      newPage = 1;
    }
    onPageChange(newPage, view, id);
  };
  const shouldRender = (idx: number) =>
    idx === 1 ||
    idx === numberOfPages ||
    idx === page ||
    (idx >= page - 1 && idx <= page + 1);
  const shouldRenderEllipsis = (idx: number) =>
    (idx === page + 2 && idx !== numberOfPages) ||
    (idx === page - 2 && idx !== 1);
  if (numberOfPages <= 1) {
    return null;
  }
  // Collect all page numbers and ellipsis to render
  const pageItems: Array<{ type: "page" | "ellipsis"; value: number | null }> =
    [];
  // Use a simple loop instead of Array(numberOfPages) to avoid "Invalid array length"
  for (let i = 1; i <= numberOfPages; i += 1) {
    if (shouldRender(i)) {
      pageItems.push({ type: "page", value: i });
    } else if (shouldRenderEllipsis(i)) {
      pageItems.push({ type: "ellipsis", value: null });
    }
  }
  // Dropdown variant - compact with chevrons only
  if (dropdown) {
    return (
      <Flex gap={2} align="center" justify="center" {...rest}>
        {/* Previous (Chevron Left) */}
        <IconButton
          aria-label="Previous page"
          icon={<ChevronLeftIcon boxSize={5} />}
          variant="ghost"
          size="sm"
          isDisabled={page === 1}
          opacity={page === 1 ? 0.4 : 1}
          cursor={page === 1 ? "not-allowed" : "pointer"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (page !== 1) {
              handlePageClick(page - 1);
            }
          }}
          _hover={{
            bg: page === 1 ? "transparent" : "gray.100",
          }}
          _disabled={{
            opacity: 0.4,
            cursor: "not-allowed",
          }}
        />
        {/* Page indicator */}
        <Box fontSize="sm" color="black" userSelect="none">
          {page} / {numberOfPages}
        </Box>
        {/* Next (Chevron Right) */}
        <IconButton
          aria-label="Next page"
          icon={<ChevronRightIcon boxSize={5} />}
          variant="ghost"
          size="sm"
          isDisabled={page === numberOfPages}
          opacity={page === numberOfPages ? 0.4 : 1}
          cursor={page === numberOfPages ? "not-allowed" : "pointer"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (page !== numberOfPages) {
              handlePageClick(page + 1);
            }
          }}
          _hover={{
            bg: page === numberOfPages ? "transparent" : "gray.100",
          }}
          _disabled={{
            opacity: 0.4,
            cursor: "not-allowed",
          }}
        />
      </Flex>
    );
  }
  // Default variant - full pagination with Previous/Next buttons
  return (
    <Flex gap={2} align="center" {...rest}>
      {/* Previous button */}
      <Button
        variant="outline"
        border={page === 1 ? "0.5px solid #E6E6E6" : "1px solid #6648FF"}
        color="#6648FF"
        bg="white"
        size="sm"
        borderRadius="8px"
        px={3}
        isDisabled={page === 1}
        opacity={page === 1 ? 0.3 : 1}
        cursor={page === 1 ? "not-allowed" : "pointer"}
        onClick={(e) => {
          e.preventDefault();
          if (page !== 1) {
            handlePageClick(page - 1);
          }
        }}
        _hover={{
          bg: page === 1 ? "white" : "white",
        }}
        _disabled={{
          opacity: 0.6,
          cursor: "not-allowed",
          border: "0.5px solid #E6E6E6",
          color: "#6648FF",
          bg: "white",
        }}
      >
        {t("translate.previous")}
      </Button>
      {/* Page numbers as connected block */}
      <Flex
        align="center"
        borderRadius="8px"
        overflow="hidden"
        border="1px solid #E6E6E6"
      >
        {pageItems?.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === pageItems.length - 1;
          const isActive = item.type === "page" && item.value === page;
          if (item?.type === "ellipsis") {
            return (
              <Box
                key={`ellipsis-${index}`}
                px={3}
                py={2}
                bg="white"
                borderRight={!isLast ? "1px solid #E6E6E6" : "none"}
                color="#666666"
                fontSize="sm"
                display="flex"
                alignItems="center"
                justifyContent="center"
                minW="40px"
              >
                ...
              </Box>
            );
          }
          return (
            <Button
              key={item.value}
              size="sm"
              borderRadius="0"
              px={3}
              bg={isActive ? "#6648FF1F" : "white"}
              color={isActive ? "#6648FF" : "#666666"}
              border="none"
              borderRight={!isLast ? "1px solid #E6E6E6" : "none"}
              _hover={{
                bg: isActive ? "#6648FF1F" : "#F5F5F5",
              }}
              _active={{
                bg: isActive ? "#6648FF1F" : "#F5F5F5",
              }}
              onClick={(e) => {
                e.preventDefault();
                if (item.value) {
                  handlePageClick(item.value);
                }
              }}
            >
              {item.value}
            </Button>
          );
        })}
      </Flex>
      {/* Next button */}
      <Button
        variant="outline"
        border={
          page === numberOfPages ? "0.5px solid #E6E6E6" : "1px solid #6648FF"
        }
        color="#6648FF"
        bg="white"
        size="sm"
        borderRadius="8px"
        px={3}
        isDisabled={page === numberOfPages}
        opacity={page === numberOfPages ? 0.3 : 1}
        cursor={page === numberOfPages ? "not-allowed" : "pointer"}
        onClick={(e) => {
          e.preventDefault();
          if (page !== numberOfPages) {
            handlePageClick(page + 1);
          }
        }}
        _hover={{
          bg: "white",
        }}
        _disabled={{
          opacity: 0.6,
          cursor: "not-allowed",
          border: "0.5px solid #E6E6E6",
          color: "#6648FF",
          bg: "white",
        }}
      >
        {t("translate.next")}
      </Button>
    </Flex>
  );
};
